import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Button,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
  Snackbar,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
  Support as SupportIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  PrivacyTip as PrivacyTipIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useLoading } from '../../contexts/LoadingContext';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationSettings {
  enabled: boolean;
  bookings: boolean;
  messages: boolean;
  rideReminders: boolean;
  promotions: boolean;
}

interface PrivacySettings {
  showPhoneNumber: boolean;
  messageSettings: 'confirmed' | 'all';
}

interface UserSettings {
  notifications: NotificationSettings;
  privacy: PrivacySettings;
  theme: 'light' | 'dark' | 'device';
  region: 'auto' | 'iran' | 'global';
}

const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const { mode, setMode, actualMode } = useThemeContext();

  const [settings, setSettings] = useState<UserSettings>({
    notifications: {
      enabled: true,
      bookings: true,
      messages: true,
      rideReminders: true,
      promotions: false,
    },
    privacy: {
      showPhoneNumber: true,
      messageSettings: 'confirmed',
    },
    theme: mode,
    region: (localStorage.getItem('user_region') as 'auto' | 'iran' | 'global') || 'auto',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteConfirmError, setDeleteConfirmError] = useState('');
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [deleteErrorDetails, setDeleteErrorDetails] = useState<{ activeBookings: number; activeRides: number } | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDeletePassword, setShowDeletePassword] = useState(false);

  const { user } = useAuth();
  const driverVerified = user?.isLicenseVerified || (user?.isDriver && user?.driverStatus === 'APPROVED');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true, 'Loading settings...');
      const response = await apiService.get('/auth/profile');
      if (response.success && response.data.user) {
        const userProfile = response.data.user;
        setSettings({
          notifications: userProfile.notificationSettings || settings.notifications,
          privacy: userProfile.privacy || settings.privacy,
          theme: userProfile.theme || settings.theme,
          region: settings.region
        });
      }
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: UserSettings) => {
    try {
      setLoading(true, 'Saving settings...');
      await apiService.put('/auth/profile', {
        notificationSettings: newSettings.notifications,
        privacy: newSettings.privacy,
        theme: newSettings.theme
      });
      setSettings(newSettings);
      setSnackbarMessage(t('settingsPage.alerts.saveSuccess', 'Settings saved successfully!'));
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Save settings error:', error);
      setSnackbarMessage(t('settingsPage.alerts.saveError', 'Failed to save settings. Please try again.'));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = (setting: keyof NotificationSettings, value: boolean) => {
    const newSettings = {
      ...settings,
      notifications: {
        ...settings.notifications,
        [setting]: value,
      },
    };

    // If disabling master notifications, turn off all sub-notifications
    if (setting === 'enabled' && !value) {
      newSettings.notifications = {
        enabled: false,
        bookings: false,
        messages: false,
        rideReminders: false,
        promotions: false,
      };
    }

    saveSettings(newSettings);
  };

  const handlePrivacyToggle = (setting: keyof PrivacySettings, value: any) => {
    const newSettings = {
      ...settings,
      privacy: {
        ...settings.privacy,
        [setting]: value,
      },
    };
    saveSettings(newSettings);
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'device') => {
    setMode(newTheme);
    const newSettings = {
      ...settings,
      theme: newTheme,
    };
    setSettings(newSettings);
    const themeName = newTheme === 'device' ? t('settingsPage.aesthetics.system', 'device mode') : t(`settingsPage.aesthetics.${newTheme}`, `${newTheme} mode`);
    setSnackbarMessage(t('settingsPage.alerts.themeChanged', 'Theme changed to {{mode}}!', { mode: themeName }));
    setSnackbarOpen(true);
  };

  const handleRegionChange = (newRegion: 'auto' | 'iran' | 'global') => {
    localStorage.setItem('user_region', newRegion);
    window.dispatchEvent(new Event('regionchange'));

    if (newRegion === 'iran') {
      localStorage.setItem('force_jalali', 'true');
    } else {
      localStorage.removeItem('force_jalali');
    }

    const newSettings = {
      ...settings,
      region: newRegion,
    };
    setSettings(newSettings);

    const regionName = newRegion === 'auto' ? t('settingsPage.region.auto', 'Automatic') : newRegion === 'iran' ? t('settingsPage.region.iran', 'Iran (Jalali)') : t('settingsPage.region.global', 'Global (Gregorian)');
    setSnackbarMessage(t('settingsPage.alerts.regionChanged', 'Region changed to {{region}}!', { region: regionName }));
    setSnackbarOpen(true);
  };

  const handleChangePassword = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPasswordDialogOpen(true);
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (passwordErrors[field as keyof typeof passwordErrors]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePassword = (password: string): string => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handlePasswordSubmit = async () => {
    // Reset errors
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };

    // Validate current password
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    // Validate new password
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else {
      const passwordError = validatePassword(passwordData.newPassword);
      if (passwordError) {
        errors.newPassword = passwordError;
      }
    }

    // Validate confirm password
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is same as current
    if (passwordData.currentPassword && passwordData.newPassword &&
      passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    // If there are errors, show them
    if (Object.values(errors).some(error => error !== '')) {
      setPasswordErrors(errors);
      return;
    }

    try {
      setLoading(true, 'Changing password...');

      const response = await apiService.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        setPasswordDialogOpen(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setSnackbarMessage(t('settingsPage.alerts.passwordSuccess', 'Password changed successfully!'));
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(response.message || 'Failed to change password');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Change password error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to change password. Please check your current password.';
      setPasswordErrors(prev => ({
        ...prev,
        currentPassword: errorMsg.includes('Current password') ? errorMsg : ''
      }));
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    setDeletePassword('');
    setDeletePasswordError('');
    setDeleteConfirmText('');
    setDeleteConfirmError('');
    setShowDeletePassword(false);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAccount = async () => {
    // Reset errors
    setDeletePasswordError('');
    setDeleteConfirmError('');

    const hasPassword = !user?.authProvider || user?.authProvider === 'LOCAL';

    // Validate password
    if (hasPassword && !deletePassword) {
      setDeletePasswordError('Password is required');
      return;
    }

    // Validate confirmation text
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setDeleteConfirmError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    try {
      setLoading(true, 'Deleting account...');

      const response = await apiService.delete('/auth/delete-account', {
        data: {
          password: hasPassword ? deletePassword : '',
          confirmDeletion: 'DELETE MY ACCOUNT'
        }
      });

      if (response.success) {
        setDeleteDialogOpen(false);
        setSnackbarMessage(t('settingsPage.alerts.accountDeleted', 'Account and all data permanently deleted. You will be logged out.'));
        setSnackbarOpen(true);

        // Clear local storage and redirect to login after a delay
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 3000);
      } else {
        setSnackbarMessage(response.message || 'Failed to delete account');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Delete account error:', error);
      const data = error.response?.data;
      if (data?.message?.includes('Invalid password')) {
        setDeletePasswordError('Incorrect password');
      } else {
        setDeleteDialogOpen(false); // Close confirmation dialog
        setDeleteErrorMessage(data?.message || 'Network error. Please try again.');
        if (data?.message?.includes('active bookings or rides')) {
          setDeleteErrorDetails({
            activeBookings: data.details?.activeBookings || 0,
            activeRides: data.details?.activeRides || 0
          });
        } else {
          setDeleteErrorDetails(null);
        }
        setDeleteErrorOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToHelp = () => {
    navigate('/help');
  };

  const handleNavigateToSupport = () => {
    navigate('/support');
  };

  const handleNavigateToTerms = () => {
    navigate('/terms');
  };

  const handleNavigateToPrivacy = () => {
    navigate('/privacy');
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    const langNames: { [key: string]: string } = {
      en: 'English',
      fr: 'French',
      fa: 'Farsi',
      ar: 'Arabic',
      es: 'Spanish',
      'zh-CN': 'Simplified Chinese',
      'zh-TW': 'Traditional Chinese',
      pa: 'Punjabi',
    };
    const langName = langNames[lng] || 'English';
    setSnackbarMessage(t('settingsPage.alerts.languageChanged', 'Language changed to {{lang}}!', { lang: langName }));
    setSnackbarOpen(true);
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 } }}>
      <Container maxWidth="lg">
        {/* Header with vibrant aesthetic */}
        <Box sx={{
          mb: 6,
          p: 4,
          borderRadius: '32px',
          background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
          color: 'white',
          boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)',
          textAlign: 'center'
        }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: '900',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.02em',
              textShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          >
            {t('settingsPage.title', '🔥 Settings & Vibes')}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', mt: 1, opacity: 0.9, fontWeight: 500 }}>
            {t('settingsPage.subtitle', 'Customize your experience to the max ⚡')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Notification Settings */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Accordion defaultExpanded sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '2.5rem' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 1 }}>
                  <NotificationsIcon sx={{ color: '#6366f1', fontSize: '2.5rem' }} />
                  <Typography sx={{ fontWeight: '900', fontSize: '2rem' }}>{t('settingsPage.notifications.title', 'Notifications 🔔')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                <List sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
                  {/* Master notification toggle */}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemIcon>
                      <NotificationsIcon sx={{ fontSize: '2rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: '1.6rem', fontWeight: 'bold' }}>{t('settingsPage.notifications.enable', 'Enable Notifications')}</Typography>}
                      secondary={<Typography sx={{ fontSize: '1.2rem' }}>{t('settingsPage.notifications.enableSub', 'Turn on/off all the alerts')}</Typography>}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={settings.notifications.enabled}
                        onChange={(e) => handleNotificationToggle('enabled', e.target.checked)}
                        sx={{
                          width: 80,
                          height: 48,
                          '& .MuiSwitch-switchBase': { padding: 0.5 },
                          '& .MuiSwitch-thumb': { width: 38, height: 38 },
                          '& .MuiSwitch-track': { borderRadius: 24 }
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <Divider sx={{ my: 1 }} />

                  {/* Individual notification settings */}
                  {settings.notifications.enabled ? (
                    <>
                      {[
                        { key: 'bookings', label: t('settingsPage.notifications.bookings', 'Bookings 🚗'), sub: t('settingsPage.notifications.bookingsSub', 'Get notified about ride bookings and updates') },
                        { key: 'messages', label: t('settingsPage.notifications.messages', 'Messages 💬'), sub: t('settingsPage.notifications.messagesSub', 'Get notified about new messages from other users') },
                        { key: 'rideReminders', label: t('settingsPage.notifications.reminders', 'Reminders ⏰'), sub: t('settingsPage.notifications.remindersSub', 'Get reminders about upcoming rides') },
                        { key: 'promotions', label: t('settingsPage.notifications.promos', 'Promos 💸'), sub: t('settingsPage.notifications.promosSub', 'Get notified about special offers') }
                      ].map((item) => (
                        <ListItem key={item.key} sx={{ py: 1.5 }}>
                          <ListItemText
                            primary={<Typography sx={{ fontSize: '1.5rem', fontWeight: '800' }}>{item.label}</Typography>}
                            secondary={<Typography sx={{ fontSize: '1.1rem' }}>{item.sub}</Typography>}
                            sx={{ ml: 4 }}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              checked={settings.notifications[item.key as keyof NotificationSettings] as boolean}
                              onChange={(e) => handleNotificationToggle(item.key as keyof NotificationSettings, e.target.checked)}
                              sx={{
                                width: 70,
                                height: 40,
                                '& .MuiSwitch-switchBase': { padding: '4px' },
                                '& .MuiSwitch-thumb': { width: 32, height: 32 },
                                '& .MuiSwitch-track': { borderRadius: 20 }
                              }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </>
                  ) : (
                    <ListItem>
                      <Alert severity="info" sx={{ width: '100%', borderRadius: '16px', fontSize: '1.2rem', p: 2 }}>
                        {t('settingsPage.notifications.disabledAlert', 'All notifications are currently disabled. Enable them to customize!')}
                      </Alert>
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Card>

          {/* Privacy Settings */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '2.5rem' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 1 }}>
                  <SecurityIcon sx={{ color: '#a855f7', fontSize: '2.5rem' }} />
                  <Typography sx={{ fontWeight: '900', fontSize: '2rem' }}>{t('settingsPage.privacy.title', 'Privacy 🛡️')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                <List sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
                  {/* Phone number visibility */}
                  {driverVerified && (
                    <ListItem sx={{ py: 2 }}>
                      <ListItemIcon>
                        <SecurityIcon sx={{ fontSize: '2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '1.6rem', fontWeight: 'bold' }}>{t('settingsPage.privacy.showPhone', 'Show Phone Number')}</Typography>}
                        secondary={<Typography sx={{ fontSize: '1.2rem' }}>{t('settingsPage.privacy.showPhoneSub', 'Allow verified passengers to see your phone number')}</Typography>}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.privacy.showPhoneNumber}
                          onChange={(e) => handlePrivacyToggle('showPhoneNumber', e.target.checked)}
                          sx={{ width: 70, height: 40, '& .MuiSwitch-thumb': { width: 32, height: 32 }, '& .MuiSwitch-track': { borderRadius: 20 } }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  )}

                  <Divider />

                  {/* Message settings */}
                  <Box>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: '900', mb: 2, ml: 2 }}>{t('settingsPage.privacy.whoCanDM', 'Who can DM me? 💬')}</Typography>
                    <Box sx={{ ml: 2, mr: 2 }}>
                      <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <RadioGroup
                          value={settings.privacy.messageSettings}
                          onChange={(e) => handlePrivacyToggle('messageSettings', e.target.value)}
                          sx={{ gap: 2 }}
                        >
                          <FormControlLabel
                            value="confirmed"
                            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />}
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Typography sx={{ fontSize: '1.4rem', fontWeight: '800' }}>{t('settingsPage.privacy.confirmedOnly', 'Only confirmed bookings 🔒')}</Typography>
                                <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                                  {t('settingsPage.privacy.confirmedOnlySub', 'Vibe check passed - only people you\'ve booked with can message you')}
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="all"
                            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />}
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Typography sx={{ fontSize: '1.4rem', fontWeight: '800' }}>{t('settingsPage.privacy.anyone', 'Anyone 🌍')}</Typography>
                                <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                                  {t('settingsPage.privacy.anyoneSub', 'Open for all - any user can start a convo with you')}
                                </Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mt: 2, borderRadius: '16px', fontSize: '1.2rem', bgcolor: 'rgba(168, 85, 247, 0.05)', color: '#a855f7' }}>
                    {t('settingsPage.privacy.protectionInfo', '💡 Your privacy is a W. We only show your data to the right people.')}
                  </Alert>
                </List>
              </AccordionDetails>
            </Accordion>
          </Card>

          {/* Theme Settings */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '2.5rem' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 1 }}>
                  <PaletteIcon sx={{ color: '#ef4444', fontSize: '2.5rem' }} />
                  <Typography sx={{ fontWeight: '900', fontSize: '2rem' }}>{t('settingsPage.aesthetics.title', 'Aesthetics ✨')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                <List sx={{ gap: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup
                        value={settings.theme}
                        onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark' | 'device')}
                        sx={{ gap: 2.5 }}
                      >
                        {[
                          { value: 'light', label: t('settingsPage.aesthetics.light', '☀️ Light Mode'), sub: t('settingsPage.aesthetics.lightSub', 'Bright and clean vibes only') },
                          // NOTE: Dark Mode & Device Mode feature toggles hiddddn until theme engine is fully implemented
                        ].map((item) => (
                          <FormControlLabel
                            key={item.value}
                            value={item.value}
                            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />}
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: '800' }}>{item.label}</Typography>
                                <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>{item.sub}</Typography>
                              </Box>
                            }
                            sx={{
                              p: 2.5,
                              m: 0,
                              borderRadius: '20px',
                              border: settings.theme === item.value ? '2px solid #ef4444' : '1px solid rgba(0,0,0,0.05)',
                              background: settings.theme === item.value ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': { background: 'rgba(0,0,0,0.02)' }
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Alert severity="info" sx={{ mt: 2, borderRadius: '16px', fontSize: '1.2rem', bgcolor: 'rgba(239, 68, 68, 0.05)', color: '#ef4444' }}>
                    Dark mode support is coming soon!
                    {mode === 'device' && (
                      <Typography sx={{ mt: 1, fontWeight: '800', fontSize: '1.1rem' }}>
                        {t('settingsPage.aesthetics.currentVibe', 'Current vibe: {{vibe}}', { vibe: actualMode === 'dark' ? '🌙 Dark' : '☀️ Light' })}
                      </Typography>
                    )}
                  </Alert>
                </List>
              </AccordionDetails>
            </Accordion>
          </Card>

          {/* Language Settings */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '2.5rem' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 1 }}>
                  <LanguageIcon sx={{ color: '#3b82f6', fontSize: '2.5rem' }} />
                  <Typography sx={{ fontWeight: '900', fontSize: '2rem' }}>{t('settingsPage.language.title', 'Language 🌐')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                <List sx={{ gap: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup
                        value={i18n.language.startsWith('zh') ? i18n.language : i18n.language.substring(0, 2)}
                        onChange={(e) => changeLanguage(e.target.value)}
                        sx={{ gap: 2.5 }}
                      >
                        {[
                          { value: 'en', label: t('settingsPage.language.english', 'English') },
                          { value: 'fr', label: t('settingsPage.language.french', 'French') },
                          { value: 'fa', label: t('settingsPage.language.farsi', 'Farsi') },
                          { value: 'ar', label: t('settingsPage.language.arabic', 'Arabic (العربية)') },
                          { value: 'es', label: t('settingsPage.language.spanish', 'Spanish (Español)') },
                          { value: 'zh-CN', label: t('settingsPage.language.chineseSimplified', 'Simplified Chinese (简体中文)') },
                          { value: 'zh-TW', label: t('settingsPage.language.chineseTraditional', 'Traditional Chinese (繁體中文)') },
                          { value: 'pa', label: t('settingsPage.language.punjabi', 'Punjabi (ਪੰਜਾਬੀ)') }
                        ].map((item) => (
                          <FormControlLabel
                            key={item.value}
                            value={item.value}
                            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />}
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: '800' }}>{item.label}</Typography>
                              </Box>
                            }
                            sx={{
                              p: 2.5,
                              m: 0,
                              borderRadius: '20px',
                              border: (i18n.language.startsWith('zh') ? i18n.language : i18n.language.substring(0, 2)) === item.value ? '2px solid #3b82f6' : '1px solid rgba(0,0,0,0.05)',
                              background: (i18n.language.startsWith('zh') ? i18n.language : i18n.language.substring(0, 2)) === item.value ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': { background: 'rgba(0,0,0,0.02)' }
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </List>
              </AccordionDetails>
            </Accordion>
          </Card>
          {/* Region Settings */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '2.5rem' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 1 }}>
                  <Typography sx={{ fontSize: '2.5rem' }}>🌍</Typography>
                  <Typography sx={{ fontWeight: '900', fontSize: '2rem' }}>{t('settingsPage.region.title', 'Region')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                <List sx={{ gap: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup
                        value={settings.region}
                        onChange={(e) => handleRegionChange(e.target.value as 'auto' | 'iran' | 'global')}
                        sx={{ gap: 2.5 }}
                      >
                        {[
                          { value: 'auto', label: t('settingsPage.region.auto', 'Automatic (Device settings)'), sub: t('settingsPage.region.autoDesc', 'Use the phone\'s default region setting.') },
                          { value: 'global', label: t('settingsPage.region.global', 'Global (Force Gregorian Calendar)'), sub: t('settingsPage.region.globalDesc', 'Forces the Gregorian calendar everywhere.') },
                          { value: 'iran', label: t('settingsPage.region.iran', 'Iran (Force Jalali Calendar)'), sub: t('settingsPage.region.iranDesc', 'Forces the Jalali (Persian) calendar when the language is set to Farsi.') }
                        ].map((item) => (
                          <FormControlLabel
                            key={item.value}
                            value={item.value}
                            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />}
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: '800' }}>{item.label}</Typography>
                                <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>{item.sub}</Typography>
                              </Box>
                            }
                            sx={{
                              p: 2.5,
                              m: 0,
                              borderRadius: '20px',
                              border: settings.region === item.value ? '2px solid #ef4444' : '1px solid rgba(0,0,0,0.05)',
                              background: settings.region === item.value ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': { background: 'rgba(0,0,0,0.02)' }
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Alert severity="warning" sx={{ mt: 2, borderRadius: '16px', fontSize: '1.2rem', color: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.05)' }}>
                    {t('settingsPage.region.disclaimer', 'Region overrides your device default. You will need to reload the app to see immediate calendar changes.')}
                  </Alert>
                </List>
              </AccordionDetails>
            </Accordion>
          </Card>

          {/* Account Management */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '2.5rem' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 1 }}>
                  <AccountIcon sx={{ color: '#f59e0b', fontSize: '2.5rem' }} />
                  <Typography sx={{ fontWeight: '900', fontSize: '2rem' }}>{t('settingsPage.account.title', 'Account Profile 👤')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                <List sx={{ gap: 2 }}>
                  <ListItemButton sx={{ borderRadius: '16px', py: 2 }}>
                    <ListItemIcon>
                      <EmailIcon sx={{ fontSize: '2rem', color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: '1.5rem', fontWeight: '800' }}>{t('settingsPage.account.editEmail', 'Edit Email 📧')}</Typography>}
                      secondary={<Typography sx={{ fontSize: '1.1rem' }}>{t('settingsPage.account.editEmailSub', 'Update the email address associated with your account')}</Typography>}
                    />
                  </ListItemButton>

                  <Divider sx={{ my: 1 }} />

                  <ListItemButton onClick={handleChangePassword} sx={{ borderRadius: '16px', py: 2 }}>
                    <ListItemIcon>
                      <LockIcon sx={{ fontSize: '2rem', color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: '1.5rem', fontWeight: '800' }}>{t('settingsPage.account.changePassword', 'Change Password 🔒')}</Typography>}
                      secondary={<Typography sx={{ fontSize: '1.1rem' }}>{t('settingsPage.account.changePasswordSub', 'Keep your account secure with a fresh pass')}</Typography>}
                    />
                  </ListItemButton>

                  <Divider sx={{ my: 1 }} />

                  <ListItemButton onClick={handleDeleteAccount} sx={{ borderRadius: '16px', py: 2, '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05)' } }}>
                    <ListItemIcon>
                      <DeleteIcon sx={{ fontSize: '2rem', color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: '1.5rem', fontWeight: '900', color: 'error.main' }}>{t('settingsPage.account.deleteAccount', 'Delete Account 💀')}</Typography>}
                      secondary={<Typography sx={{ fontSize: '1.1rem' }}>{t('settingsPage.account.deleteAccountSub', 'Permanently yeet your data from our servers')}</Typography>}
                    />
                  </ListItemButton>

                  <Alert severity="warning" sx={{ mt: 3, borderRadius: '16px', fontSize: '1.1rem' }}>
                    {t('settingsPage.account.privacyRegulations', '🛡️ Privacy first - we comply with GDPR and global data protection regulations. All data is removed if you choose to leave.')}
                  </Alert>
                </List>
              </AccordionDetails>
            </Accordion>
          </Card>

          {/* Other Settings */}
          <Card elevation={0} sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)',
            bgcolor: actualMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
            mb: 4
          }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Typography sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2.5, fontWeight: '900', fontSize: '2rem' }}>
                <SettingsIcon sx={{ color: '#10b981', fontSize: '2.5rem' }} />
                {t('settingsPage.support.title', 'Help & Support 🤝')}
              </Typography>

              <List sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
                <ListItemButton onClick={handleNavigateToHelp} sx={{ borderRadius: '16px', py: 2.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <ListItemIcon>
                    <HelpIcon sx={{ fontSize: '2.5rem', color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.6rem', fontWeight: '800' }}>{t('settingsPage.support.helpCenter', 'Help Center & FAQ 📚')}</Typography>}
                    secondary={<Typography sx={{ fontSize: '1.2rem' }}>{t('settingsPage.support.helpCenterSub', 'Got questions? We\'ve got answers.')}</Typography>}
                  />
                </ListItemButton>

                <ListItemButton onClick={handleNavigateToSupport} sx={{ borderRadius: '16px', py: 2.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <ListItemIcon>
                    <SupportIcon sx={{ fontSize: '2.5rem', color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.6rem', fontWeight: '800' }}>{t('settingsPage.support.contact', 'Contact Support 🎧')}</Typography>}
                    secondary={<Typography sx={{ fontSize: '1.2rem' }}>{t('settingsPage.support.contactSub', 'Talk to real humans, no cap.')}</Typography>}
                  />
                </ListItemButton>

                <ListItemButton onClick={handleNavigateToTerms} sx={{ borderRadius: '16px', py: 2.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <ListItemIcon>
                    <DescriptionIcon sx={{ fontSize: '2.5rem', color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.6rem', fontWeight: '800' }}>{t('settingsPage.support.terms', 'Terms of Service 📜')}</Typography>}
                    secondary={<Typography sx={{ fontSize: '1.2rem' }}>{t('settingsPage.support.termsSub', 'Read our terms and conditions')}</Typography>}
                  />
                </ListItemButton>

                <ListItemButton onClick={handleNavigateToPrivacy} sx={{ borderRadius: '16px', py: 2.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <ListItemIcon>
                    <PrivacyTipIcon sx={{ fontSize: '2.5rem', color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.6rem', fontWeight: '800' }}>{t('settingsPage.support.privacy', 'Privacy Policy 🔐')}</Typography>}
                    secondary={<Typography sx={{ fontSize: '1.2rem' }}>{t('settingsPage.support.privacySub', 'Learn how we protect your data')}</Typography>}
                  />
                </ListItemButton>
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Change Password Dialog */}
        <Dialog
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '32px', p: 2 } }}
        >
          <DialogTitle sx={{ fontSize: '2rem', fontWeight: '900', textAlign: 'center' }}>{t('settingsPage.passwordDialog.title', 'Secure Your Vibe 🔒')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 4, fontSize: '1.3rem', textAlign: 'center', opacity: 0.8 }}>
              {t('settingsPage.passwordDialog.subtitle', 'Change your pass with a new secure entry.')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('settingsPage.passwordDialog.currentLabel', 'Normal Current Password')}
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                error={!!passwordErrors.currentPassword}
                helperText={passwordErrors.currentPassword}
                InputProps={{
                  sx: { fontSize: '1.4rem', borderRadius: '16px' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
              />

              <TextField
                fullWidth
                label={t('settingsPage.passwordDialog.newLabel', 'New Epic Password')}
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword || t('settingsPage.passwordDialog.helperText', 'Min 8 chars, uppercase, lowercase, numbers (main character energy only)')}
                InputProps={{
                  sx: { fontSize: '1.4rem', borderRadius: '16px' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
              />

              <TextField
                fullWidth
                label={t('settingsPage.passwordDialog.confirmLabel', 'Double Check Password')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                error={!!passwordErrors.confirmPassword}
                helperText={passwordErrors.confirmPassword}
                InputProps={{
                  sx: { fontSize: '1.4rem', borderRadius: '16px' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 4, borderRadius: '16px', fontSize: '1.1rem' }}>
              {t('settingsPage.passwordDialog.logoutInfo', '🔒 You\'ll be logged out after this. No worries though, just log back in!')}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 0, justifyContent: 'center', gap: 2 }}>
            <Button onClick={() => setPasswordDialogOpen(false)} sx={{ fontSize: '1.4rem', px: 4, py: 1.5, borderRadius: '20px' }}>
              {t('settingsPage.passwordDialog.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handlePasswordSubmit}
              variant="contained"
              disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              sx={{
                fontSize: '1.4rem',
                px: 6,
                py: 1.5,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              }}
            >
              {t('settingsPage.passwordDialog.update', 'Update Pass 🚀')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '32px', p: 2 } }}
        >
          <DialogTitle sx={{ fontSize: '3rem', fontWeight: '900', color: 'error.main', textAlign: 'center' }}>
            {t('settingsPage.deleteDialog.title', 'Final Yeet? 💀')}
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 4, borderRadius: '20px', p: 2 }}>
              <Typography sx={{ fontWeight: '900', fontSize: '2.1rem', mb: 1 }}>
                {t('settingsPage.deleteDialog.warningTitle', '⚠️ This is irreversible!')}
              </Typography>
              <Typography sx={{ fontSize: '1.65rem' }}>
                {t('settingsPage.deleteDialog.warningText', 'Deleting your account will permanently remove:')}
              </Typography>
              <ul style={{ fontSize: '1.5rem', marginTop: '12px' }}>
                <li>{t('settingsPage.deleteDialog.listInfo', 'All your personal info, ride history, bookings, messages, etc.')}</li>
              </ul>
            </Alert>

            <Typography sx={{ mb: 3, fontSize: '1.8rem', textAlign: 'center', opacity: 0.8 }}>
              {(!user?.authProvider || user?.authProvider === 'LOCAL')
                ? t('settingsPage.deleteDialog.confirmInfo', 'To confirm, type your password and the secret phrase below.')
                : t('settingsPage.deleteDialog.confirmInfoOAuth', 'To confirm, type the secret phrase below.')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(!user?.authProvider || user?.authProvider === 'LOCAL') && (
                <TextField
                  fullWidth
                  label={t('settingsPage.deleteDialog.passwordLabel', 'Your Password')}
                  type={showDeletePassword ? 'text' : 'password'}
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    if (deletePasswordError) setDeletePasswordError('');
                  }}
                  error={!!deletePasswordError}
                  helperText={deletePasswordError}
                  InputProps={{
                    sx: { fontSize: '2.1rem', borderRadius: '16px' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle delete password visibility"
                          onClick={() => setShowDeletePassword(!showDeletePassword)}
                          edge="end"
                          sx={{ color: theme.palette.text.secondary, mr: 1 }}
                        >
                          {showDeletePassword ? <VisibilityOff sx={{ fontSize: '2.5rem' }} /> : <Visibility sx={{ fontSize: '2.5rem' }} />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  InputLabelProps={{ sx: { fontSize: '1.8rem' } }}
                />
              )}

              <TextField
                fullWidth
                label={t('settingsPage.deleteDialog.phraseLabel', 'Type DELETE MY ACCOUNT')}
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                  if (deleteConfirmError) setDeleteConfirmError('');
                }}
                error={!!deleteConfirmError}
                helperText={deleteConfirmError || t('settingsPage.deleteDialog.phraseHelper', 'Type exactly: DELETE MY ACCOUNT')}
                placeholder="DELETE MY ACCOUNT"
                InputProps={{ sx: { fontSize: '2.1rem', borderRadius: '16px' } }}
                InputLabelProps={{ sx: { fontSize: '1.8rem' } }}
              />
            </Box>

            <Alert severity="warning" sx={{ mt: 4, borderRadius: '16px', fontSize: '1.5rem' }}>
              {t('settingsPage.deleteDialog.privacyInfo', '🛡️ Your privacy matters! We comply with global data laws.')}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 0, justifyContent: 'center', gap: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontSize: '2.1rem', px: 4, py: 1.5, borderRadius: '20px' }}>
              {t('settingsPage.deleteDialog.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={confirmDeleteAccount}
              variant="contained"
              disabled={((!user?.authProvider || user?.authProvider === 'LOCAL') && !deletePassword) || deleteConfirmText !== 'DELETE MY ACCOUNT'}
              sx={{
                fontSize: '2.1rem',
                px: 6,
                py: 1.5,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
              }}
            >
              {t('settingsPage.deleteDialog.delete', 'Delete Forever 💀')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Account Error Dialog */}
        <Dialog
          open={deleteErrorOpen}
          onClose={() => setDeleteErrorOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '32px', p: 3 } }}
        >
          <DialogTitle sx={{ fontSize: '2.5rem', fontWeight: '900', color: 'error.main', textAlign: 'center', pb: 2 }}>
            ⚠️ Cannot Delete Account
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '1.8rem', textAlign: 'center', mb: 3, opacity: 0.9 }}>
              {deleteErrorMessage}
            </Typography>
            {deleteErrorDetails && (
              <Box sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                p: 3,
                borderRadius: '20px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                mb: 2
              }}>
                {deleteErrorDetails.activeBookings > 0 && (
                  <Typography sx={{ fontSize: '1.65rem', fontWeight: '700', color: 'error.main', mb: deleteErrorDetails.activeRides > 0 ? 1.5 : 0 }}>
                    • Active Bookings: {deleteErrorDetails.activeBookings}
                  </Typography>
                )}
                {deleteErrorDetails.activeRides > 0 && (
                  <Typography sx={{ fontSize: '1.65rem', fontWeight: '700', color: 'error.main' }}>
                    • Active Rides: {deleteErrorDetails.activeRides}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', p: 2, pt: 0 }}>
            <Button
              onClick={() => setDeleteErrorOpen(false)}
              variant="contained"
              sx={{
                fontSize: '1.8rem',
                fontWeight: '800',
                px: 5,
                py: 1.5,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={<Typography sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>{snackbarMessage}</Typography>}
          ContentProps={{ sx: { borderRadius: '16px', py: 2, px: 3 } }}
        />
      </Container>
    </Box>
  );
};

export default SettingsPage;